import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// ---- Types ----
type FabbricaRow = Database['public']['Tables']['fabbriche']['Row'];
type MarchioRow = Database['public']['Tables']['marchi']['Row'];
type CategoriaRow = Database['public']['Tables']['categorie_prodotto']['Row'];
type ServizioRow = Database['public']['Tables']['servizi']['Row'];

export type PublicFabbrica = FabbricaRow;
export type PublicMarchio = MarchioRow & {
  categorie?: CategoriaRow[];
  carousel_logo_url: string | null;
  page_logo_url: string | null;
};
export type PublicCategoria = CategoriaRow;
export type PublicServizio = ServizioRow;

// ---- Fabbriche (public, active only) ----
export function usePublicFabbriche() {
  return useQuery({
    queryKey: ['public', 'fabbriche'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fabbriche')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data as PublicFabbrica[];
    },
  });
}

// ---- Marchi (public, active only, with categories) ----
export function usePublicMarchi() {
  return useQuery({
    queryKey: ['public', 'marchi'],
    queryFn: async () => {
      // Fetch marchi
      const { data: marchi, error: mErr } = await supabase
        .from('marchi')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (mErr) throw mErr;

      // Fetch all marchi_categorie links
      const { data: links, error: lErr } = await supabase
        .from('marchi_categorie')
        .select('marchio_id, categoria_id');
      if (lErr) throw lErr;

      // Fetch all active categories
      const { data: categorie, error: cErr } = await supabase
        .from('categorie_prodotto')
        .select('*')
        .eq('is_active', true);
      if (cErr) throw cErr;

      const catMap = new Map(categorie.map(c => [c.id, c]));

      return (marchi as MarchioRow[]).map(m => {
        const catIds = links.filter(l => l.marchio_id === m.id).map(l => l.categoria_id);
        return {
          ...m,
          carousel_logo_url: (m as MarchioRow & { carousel_logo_url?: string | null }).carousel_logo_url ?? m.logo_url,
          page_logo_url: (m as MarchioRow & { page_logo_url?: string | null }).page_logo_url ?? m.logo_url,
          categorie: catIds.map(id => catMap.get(id)).filter(Boolean) as CategoriaRow[],
        };
      }) as PublicMarchio[];
    },
  });
}

// ---- Categorie (public, active only) ----
export function usePublicCategorie() {
  return useQuery({
    queryKey: ['public', 'categorie_prodotto'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorie_prodotto')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data as PublicCategoria[];
    },
  });
}

// ---- Servizi (public, active only) ----
export function usePublicServizi() {
  return useQuery({
    queryKey: ['public', 'servizi'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('servizi')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data as PublicServizio[];
    },
  });
}
